import { Fragment, useEffect, useState } from "react";
import axios from "axios";
import { csvParse } from "d3";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faChevronDown,
  faChevronUp,
  faTriangleExclamation,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import { useContextStateManagement } from "../context/ContextApi";
import ColdTransferFunctionsCsv from "../assets/bulkUploadLeadFiles/ColdTransferFunctions.csv";

const REQUIRED_HEADERS = [
  "description",
  "handover_receiver_phone_number",
  "transfer_line",
];

const OPTIONAL_HEADERS = ["name", "ext"];
const ALLOWED_HEADERS = new Set([
  ...REQUIRED_HEADERS,
  ...OPTIONAL_HEADERS,
]);

const normalizeHeader = (value) => String(value || "").trim().toLowerCase();
const normalizeCell = (value) =>
  value == null ? "" : String(value).trim();

const isBlankRow = (row) =>
  Object.values(row).every((value) => normalizeCell(value) === "");

export const ColdTransferCsvUpload = ({
  memberType,
  onImported,
  onTokenExpired,
  resetKey,
}) => {
  const { PracticeId, token } = useContextStateManagement();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [parsedRows, setParsedRows] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);
  const [expandedPreviewRows, setExpandedPreviewRows] = useState([]);
  const [rowErrors, setRowErrors] = useState([]);
  const [generalError, setGeneralError] = useState("");
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const resetUploadState = () => {
    setSelectedFileName("");
    setParsedRows([]);
    setPreviewRows([]);
    setExpandedPreviewRows([]);
    setRowErrors([]);
    setGeneralError("");
    setIsPreviewLoading(false);
    setIsImporting(false);
  };

  useEffect(() => {
    resetUploadState();
    setIsOpen(false);
  }, [resetKey]);

  const handleApiError = (error, fallbackMessage) => {
    const responseData = error?.response?.data || {};
    if (responseData?.error === "Token is expired, login again") {
      onTokenExpired?.();
      return;
    }

    setRowErrors(responseData.rowErrors || []);
    setGeneralError(responseData.error || fallbackMessage);
  };

  const runPreview = async (rows) => {
    setIsPreviewLoading(true);
    setRowErrors([]);
    setGeneralError("");
    setPreviewRows([]);
    setExpandedPreviewRows([]);

    try {
      const response = await axios.post(
        "/api/apiFunction/bulkUploadColdTransfer",
        {
          practiceId: PracticeId,
          memberType,
          rows,
          dryRun: true,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setPreviewRows(response.data?.previewRows || []);
    } catch (error) {
      handleApiError(error, "Unable to validate the CSV file.");
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    resetUploadState();

    if (!file) {
      return;
    }

    if (!file.name.toLowerCase().endsWith(".csv")) {
      setGeneralError("Please upload a CSV file.");
      return;
    }

    try {
      const text = await file.text();
      const parsed = csvParse(text);
      const rawColumns = parsed.columns || [];
      const normalizedColumns = rawColumns.map(normalizeHeader);

      if (normalizedColumns.length === 0) {
        setGeneralError("The CSV file is empty.");
        return;
      }

      if (new Set(normalizedColumns).size !== normalizedColumns.length) {
        setGeneralError("CSV header names must be unique.");
        return;
      }

      const unknownHeaders = normalizedColumns.filter(
        (header) => header && !ALLOWED_HEADERS.has(header),
      );
      if (unknownHeaders.length > 0) {
        setGeneralError(
          `Unsupported column(s): ${unknownHeaders.join(", ")}.`,
        );
        return;
      }

      const missingHeaders = REQUIRED_HEADERS.filter(
        (header) => !normalizedColumns.includes(header),
      );
      if (missingHeaders.length > 0) {
        setGeneralError(
          `Missing required column(s): ${missingHeaders.join(", ")}.`,
        );
        return;
      }

      const mappedRows = parsed
        .map((row) => {
          const normalizedRow = {
            name: "",
            description: "",
            handover_receiver_phone_number: "",
            ext: "",
            transfer_line: "",
          };

          rawColumns.forEach((columnName, index) => {
            const normalizedHeader = normalizedColumns[index];
            if (!ALLOWED_HEADERS.has(normalizedHeader)) {
              return;
            }
            normalizedRow[normalizedHeader] = normalizeCell(row[columnName]);
          });

          return normalizedRow;
        })
        .filter((row) => !isBlankRow(row));

      if (mappedRows.length === 0) {
        setGeneralError("The CSV file does not contain any non-empty rows.");
        return;
      }

      setSelectedFileName(file.name);
      setParsedRows(mappedRows);
      await runPreview(mappedRows);
    } catch (error) {
      setGeneralError("Unable to read the CSV file.");
    } finally {
      event.target.value = "";
    }
  };

  const handleImport = async () => {
    if (parsedRows.length === 0) {
      return;
    }

    setIsImporting(true);
    setRowErrors([]);
    setGeneralError("");

    try {
      const response = await axios.post(
        "/api/apiFunction/bulkUploadColdTransfer",
        {
          practiceId: PracticeId,
          memberType,
          rows: parsedRows,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      resetUploadState();
      setIsOpen(false);
      onImported?.(response.data);
    } catch (error) {
      handleApiError(error, "Unable to import the CSV file.");
    } finally {
      setIsImporting(false);
    }
  };

  const togglePreviewRow = (rowNumber) => {
    setExpandedPreviewRows((currentRows) =>
      currentRows.includes(rowNumber)
        ? currentRows.filter((value) => value !== rowNumber)
        : [...currentRows, rowNumber],
    );
  };

  const previewSubset = previewRows.slice(0, 8);

  return (
    <div className="mt-4 border rounded p-3">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div>
          <div className="fw-semibold">Bulk Upload Cold Transfers</div>
          <div className="form-text">
            Upload a CSV using the same fields as the cold transfer form.
          </div>
        </div>

        <button
          type="button"
          className="simple-btn"
          onClick={() => {
            setIsOpen((currentValue) => !currentValue);
            setGeneralError("");
          }}
        >
          <FontAwesomeIcon icon={faUpload} className="me-2" />
          {isOpen ? "Hide Upload" : "Upload CSV"}
        </button>
      </div>

      {isOpen && (
        <>
          <div className="mt-3 d-flex flex-wrap align-items-center gap-2">
            <input
              type="file"
              accept=".csv"
              className="form-control"
              style={{ maxWidth: 340 }}
              onChange={handleFileChange}
            />
            <a
              href={ColdTransferFunctionsCsv}
              download="ColdTransferFunctions.csv"
              className="simple-btn"
            >
              Download Sample CSV
            </a>
          </div>

          <div className="form-text mt-2">
            Required columns: <code>description</code>,{" "}
            <code>handover_receiver_phone_number</code>, and{" "}
            <code>transfer_line</code>. Optional columns: <code>name</code> and{" "}
            <code>ext</code>. Blank names are generated from the description.
          </div>

          {selectedFileName && (
            <div className="mt-2 small">Loaded file: {selectedFileName}</div>
          )}

          {isPreviewLoading && (
            <div className="mt-3 d-flex align-items-center">
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <span>Validating CSV rows...</span>
            </div>
          )}

          {generalError && (
            <div className="mt-3 text-danger">
              <FontAwesomeIcon icon={faTriangleExclamation} className="me-2" />
              {generalError}
            </div>
          )}

          {rowErrors.length > 0 && (
            <div className="mt-3">
              <div className="text-danger fw-semibold">
                Fix these rows before importing.
              </div>
              <div
                className="mt-2 small"
                style={{ maxHeight: 180, overflowY: "auto" }}
              >
                {rowErrors.map((errorItem, index) => (
                  <div key={`${errorItem.rowNumber}-${errorItem.field}-${index}`}>
                    Row {errorItem.rowNumber} ({errorItem.field}):{" "}
                    {errorItem.message}
                  </div>
                ))}
              </div>
            </div>
          )}

          {previewRows.length > 0 && (
            <div className="mt-3">
              <div className="d-flex align-items-center text-success">
                <FontAwesomeIcon icon={faCircleCheck} className="me-2" />
                {previewRows.length} cold transfer
                {previewRows.length === 1 ? "" : "s"} ready to import.
              </div>

              <div className="form-text mt-2">
                The preview stays compact by default. Use{" "}
                <span className="fw-semibold">View details</span> on any row to
                inspect the description, transfer line, and extension before
                importing.
              </div>

              <div className="table-responsive mt-3">
                <table
                  className="table table-sm align-middle"
                  style={{ color: "#9da7aa" }}
                >
                  <thead>
                    <tr>
                      <th scope="col">Row</th>
                      <th scope="col">Function Name</th>
                      <th scope="col">Receiver Number</th>
                      <th scope="col" className="text-end">
                        Details
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewSubset.map((row) => {
                      const isExpanded = expandedPreviewRows.includes(
                        row.rowNumber,
                      );

                      return (
                        <Fragment key={row.rowNumber}>
                          <tr>
                            <td>{row.rowNumber}</td>
                            <td>{row.name}</td>
                            <td>{row.handoverReceiverPhoneNumber}</td>
                            <td className="text-end">
                              <button
                                type="button"
                                className="simple-btn"
                                onClick={() => togglePreviewRow(row.rowNumber)}
                              >
                                <FontAwesomeIcon
                                  icon={
                                    isExpanded ? faChevronUp : faChevronDown
                                  }
                                  className="me-2"
                                />
                                {isExpanded
                                  ? "Hide details"
                                  : "View details"}
                              </button>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={4} className="pt-0">
                                <div className="border rounded p-3 bg-dark bg-opacity-10">
                                  <div className="mb-2">
                                    <div className="fw-semibold text-light-emphasis">
                                      Description
                                    </div>
                                    <div>{row.description || "-"}</div>
                                  </div>
                                  <div className="mb-2">
                                    <div className="fw-semibold text-light-emphasis">
                                      Transfer Line
                                    </div>
                                    <div>{row.transferLine || "-"}</div>
                                  </div>
                                  <div>
                                    <div className="fw-semibold text-light-emphasis">
                                      Extension
                                    </div>
                                    <div>{row.ext || "-"}</div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {previewRows.length > previewSubset.length && (
                <div className="small">
                  Showing the first {previewSubset.length} rows of{" "}
                  {previewRows.length}.
                </div>
              )}

              <div className="mt-3 d-flex align-items-center gap-2">
                <button
                  type="button"
                  className={`simple-btn ${isImporting ? "px-4" : ""}`}
                  onClick={handleImport}
                  disabled={isImporting || isPreviewLoading}
                >
                  {isImporting ? (
                    <div className="spinner-border spinner-border-sm" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  ) : (
                    "Import Cold Transfers"
                  )}
                </button>

                <button
                  type="button"
                  className="simple-btn"
                  onClick={() => {
                    resetUploadState();
                    setIsOpen(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
